import React from "react";
import { Row, Col, Form } from "react-bootstrap";
import Select from "react-select";
import type { BatchFee, PlanField } from "../../../../api/academyEnrolments/enrolmentsService";

const FREQUENCY_LABELS: Record<string, string> = {
  ONCE_A_WEEK: "Once a week",
  TWICE_A_WEEK: "Twice a week",
  THRICE_A_WEEK: "Thrice a week",
  FOUR_TIMES_A_WEEK: "4x a week",
  FIVE_TIMES_A_WEEK: "5x a week",
  SIX_TIMES_A_WEEK: "6x a week",
  DAILY: "Daily",
};

interface FrequencyAndPaymentSectionProps {
  fees: BatchFee[];
  selectedTrainingFrequency: string;
  selectedPaymentFrequency: string;
  availablePaymentFrequencies: string[];
  planFields: PlanField[];
  onTrainingFrequencySelect: (f: string) => void;
  onPaymentFrequencySelect: (f: string) => void;
  disabled?: boolean;
  sectionNumber?: number;
}

const FrequencyAndPaymentSection: React.FC<FrequencyAndPaymentSectionProps> = ({
  fees,
  selectedTrainingFrequency,
  selectedPaymentFrequency,
  availablePaymentFrequencies,
  planFields,
  onTrainingFrequencySelect,
  onPaymentFrequencySelect,
  disabled,
  sectionNumber = 8,
}) => {
  const fieldByFeeKey = new Map(planFields.map((field) => [field.feeKey, field]));
  const fieldByFrequency = new Map(planFields.map((field) => [field.frequency, field]));

  const trainingOptions = fees.map((f) => ({
    value: f.trainingFrequency,
    label: FREQUENCY_LABELS[f.trainingFrequency] ?? f.trainingFrequency,
    fee: f,
  }));

  // availablePaymentFrequencies are feeInfo keys (e.g. "monthlyFee")
  const paymentOptions = availablePaymentFrequencies.map((key) => ({
    value: fieldByFeeKey.get(key)?.frequency ?? key,
    label: fieldByFeeKey.get(key)?.label ?? key,
  }));

  // Also build payment options from already-selected training freq in case hydration comes in API values
  const allPaymentOptions = (() => {
    const fromAvailable = paymentOptions;
    if (selectedPaymentFrequency && !fromAvailable.find((o) => o.value === selectedPaymentFrequency)) {
      return [
        ...fromAvailable,
        {
          value: selectedPaymentFrequency,
          label: fieldByFrequency.get(selectedPaymentFrequency)?.label ?? selectedPaymentFrequency,
        },
      ];
    }
    return fromAvailable;
  })();

  return (
    <div className="enrolment-form-section">
      <h6 className="enrolment-section-title">
        <span className="enrolment-section-number">{sectionNumber}</span>
        Training &amp; Payment Frequency
      </h6>

      <Row className="g-3">
        <Col xs={12} md={5}>
          <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
            Training Frequency *
          </Form.Label>
          {disabled ? (
            <Form.Control
              readOnly
              value={(FREQUENCY_LABELS[selectedTrainingFrequency] ?? selectedTrainingFrequency) || "—"}
            />
          ) : (
            <Select
              classNamePrefix="react-select"
              options={trainingOptions}
              isDisabled={disabled}
              value={
                selectedTrainingFrequency
                  ? {
                      value: selectedTrainingFrequency,
                      label: FREQUENCY_LABELS[selectedTrainingFrequency] ?? selectedTrainingFrequency,
                      fee: fees.find((f) => f.trainingFrequency === selectedTrainingFrequency)!,
                    }
                  : null
              }
              onChange={(opt) => opt && onTrainingFrequencySelect(opt.value)}
              placeholder="Select frequency..."
            />
          )}
        </Col>

        {selectedTrainingFrequency && (
          <Col xs={12} md={5}>
            <Form.Label className="mb-1 text-muted" style={{ fontSize: 12 }}>
              Payment Frequency *
            </Form.Label>
            {disabled ? (
              <Form.Control
                readOnly
                value={
                  (fieldByFrequency.get(selectedPaymentFrequency)?.label ??
                    fieldByFeeKey.get(selectedPaymentFrequency)?.label ??
                    selectedPaymentFrequency) || "—"
                }
              />
            ) : (
              <Select
                classNamePrefix="react-select"
                options={allPaymentOptions}
                isDisabled={disabled || allPaymentOptions.length === 0}
                value={
                  selectedPaymentFrequency
                    ? allPaymentOptions.find((o) => o.value === selectedPaymentFrequency) ?? {
                        value: selectedPaymentFrequency,
                        label: selectedPaymentFrequency,
                      }
                    : null
                }
                onChange={(opt) => opt && onPaymentFrequencySelect(opt.value)}
                placeholder="Select payment plan..."
              />
            )}
          </Col>
        )}
      </Row>
    </div>
  );
};

export default FrequencyAndPaymentSection;
